import { useState, useEffect } from "react";
import { ISaveDraftLabGridProps } from "../../typings/models/SaveDraftGridProps";
import "../../styles/LabStyles.css";
import SaveDraftLabGridRow from "./SaveDrafLabGridRow";
import { DeleteDraftApplications } from "../../api/apimethods";
import { NotifyType } from "../../utils/app-config";
import { notify } from "../../utils";
import Loader from "../../components/molecules/Loader";
const SaveDraftLabGrid: React.FC<ISaveDraftLabGridProps> = ({
  data,
  isLoading,
  setData,
  headerColumns,
  setShowGrid,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoaading, setIsLoaading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [btnNameForLoader, setBtnNameForLoader] = useState<string>("");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;
  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleLoader = (enableLoader: boolean, btnNameOfLoader: string) => {
    setBtnNameForLoader(enableLoader ? btnNameOfLoader : "");
  };
  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
    )
  );

  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const { message } = notify();
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    try {
      handleLoader(true, "Deleting...");

      for (const selectedRow of selectedRows) {
        const response = await DeleteDraftApplications(selectedRow);
      }

      setSelectedRows([]);
      message(NotifyType.Success, "Draft Deleted Successfully");
      setShowGrid(false);
    } catch (error) {
      console.error("Delete error:", error);
      message(NotifyType.Error, "Error deleting drafts");
    } finally {
      handleLoader(false, "");
    }
  };

  const handleCheckboxChange = (applicationNumber: string) => {
    setSelectedRows((prevRows) => {
      if (prevRows.includes(applicationNumber)) {
        return prevRows.filter((num) => num !== applicationNumber);
      } else {
        return [...prevRows, applicationNumber];
      }
    });
  };
  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };

  return (
    <div>
      <div className="info-content">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearch}
          className="form-control mb-3 search-input"
        />

        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {headerColumns.map((column, index) => (
                <th
                  key={index}
                  className="grid-header"
                  onClick={() => handleSort(column.sortKey)}
                >
                  {column.columnName}
                  {sortColumn === column.sortKey && (
                    <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToShow.map((item, ind) => (
              <SaveDraftLabGridRow
                key={ind}
                data={{ ...item, sno: startIndex + ind + 1 }}
                sno={ind + 1}
                onCheckboxChange={handleCheckboxChange}
                isChecked={selectedRows.includes(item.applicationNumber)}
              />
            ))}
          </tbody>
        </table>
        {isLoading && <Loader />}

        <nav aria-label="Page navigation example">
          <button
            type="button"
            className={`delete ${btnNameForLoader} custom-search-button`}
            onClick={handleDelete}
            disabled={selectedRows.length === 0 || isLoaading}
          >
            {btnNameForLoader || "Delete"}
          </button>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {renderPageButtons()}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <br />
    </div>
  );
};
export default SaveDraftLabGrid;
